import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { KeyRound, Download, LogOut, ArrowLeft, LoaderCircle, Users } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { ASSETS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "bt_admin_key";

const AdminPage = () => {
    const [key, setKey] = useState("");
    const [subscribers, setSubscribers] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async (accessKey) => {
        setLoading(true);
        setError("");
        try {
            const { data } = await axios.get(`${API}/subscribers`, {
                headers: { "X-Admin-Key": accessKey },
            });
            setSubscribers(data.subscribers || []);
            sessionStorage.setItem(SESSION_KEY, accessKey);
        } catch (err) {
            setSubscribers(null);
            sessionStorage.removeItem(SESSION_KEY);
            if (err.response?.status === 403) {
                setError("Invalid access key. Please try again.");
            } else {
                setError("Could not load subscribers. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const saved = sessionStorage.getItem(SESSION_KEY);
        if (saved) load(saved);
    }, []);

    const submit = (e) => {
        e.preventDefault();
        if (key.trim()) load(key.trim());
    };

    const downloadCsv = () => {
        const rows = ["email,subscribed_at"];
        subscribers.forEach((s) => rows.push(`${s.email},${s.subscribed_at}`));
        const blob = new Blob([rows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "brothatruth-subscribers.csv";
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Subscriber list downloaded.");
    };

    const logout = () => {
        sessionStorage.removeItem(SESSION_KEY);
        setSubscribers(null);
        setKey("");
    };

    return (
        <div data-testid="admin-page" className="relative min-h-screen overflow-hidden bg-[#080B12] px-5 py-10">
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute -top-32 left-1/2 h-[420px] w-[720px] -translate-x-1/2 rounded-full bg-[#7200C8]/20 blur-[150px]" />
            </div>

            <div className="relative mx-auto max-w-3xl">
                <div className="flex items-center justify-between">
                    <a
                        data-testid="admin-back-home"
                        href="/"
                        className="flex items-center gap-2 text-sm text-[#F8F5EE]/60 transition-colors hover:text-[#F4C46B]"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back to site
                    </a>
                    <img src={ASSETS.monogramWhite} alt="BrothaTruth BT logo" className="h-12 w-auto" />
                </div>

                {subscribers === null ? (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mx-auto mt-20 max-w-md rounded-3xl border border-[#D9A441]/30 bg-[#0B0E18]/80 p-8 backdrop-blur-xl sm:p-10"
                    >
                        <div className="text-center">
                            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-gold-gradient">
                                <KeyRound className="h-6 w-6 text-[#080B12]" />
                            </span>
                            <h1 className="mt-5 font-display text-2xl font-bold text-[#F8F5EE]">
                                Subscriber <span className="text-gold-gradient">List</span>
                            </h1>
                            <p className="mt-2 text-sm text-[#F8F5EE]/55">Private access. Enter your admin key to continue.</p>
                        </div>

                        <form data-testid="admin-login-form" onSubmit={submit} className="mt-8 space-y-4">
                            <label htmlFor="admin-key" className="sr-only">Admin access key</label>
                            <input
                                id="admin-key"
                                data-testid="admin-key-input"
                                type="password"
                                value={key}
                                onChange={(e) => setKey(e.target.value)}
                                placeholder="Enter admin access key"
                                className="w-full rounded-full border border-[#D9A441]/30 bg-[#080B12] px-5 py-3.5 text-sm text-[#F8F5EE] placeholder:text-[#F8F5EE]/35 focus:border-[#D9A441] focus:outline-none"
                                autoComplete="off"
                            />
                            {error && (
                                <p data-testid="admin-error" className="text-center text-sm text-red-400">{error}</p>
                            )}
                            <button
                                data-testid="admin-login-button"
                                type="submit"
                                disabled={loading}
                                className="btn-shine flex w-full items-center justify-center gap-2 rounded-full bg-gold-gradient px-8 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-[#080B12] transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
                            >
                                {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                                {loading ? "Checking" : "Unlock List"}
                            </button>
                        </form>
                    </motion.div>
                ) : (
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="mt-12"
                    >
                        <div className="flex flex-wrap items-center justify-between gap-4">
                            <div className="flex items-center gap-3">
                                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-gold-gradient">
                                    <Users className="h-5 w-5 text-[#080B12]" />
                                </span>
                                <div>
                                    <h1 className="font-display text-xl font-bold text-[#F8F5EE]">Community Subscribers</h1>
                                    <p data-testid="admin-count" className="text-sm text-[#D9A441]">
                                        {subscribers.length} {subscribers.length === 1 ? "member" : "members"}
                                    </p>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    data-testid="admin-download-button"
                                    onClick={downloadCsv}
                                    className="btn-shine flex items-center gap-2 rounded-full bg-gold-gradient px-5 py-2.5 text-xs font-bold uppercase tracking-[0.12em] text-[#080B12] transition-transform duration-300 hover:scale-[1.03]"
                                >
                                    <Download className="h-4 w-4" />
                                    Download CSV
                                </button>
                                <button
                                    data-testid="admin-logout-button"
                                    onClick={logout}
                                    className="flex items-center gap-2 rounded-full border border-[#D9A441]/40 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.12em] text-[#F4C46B] transition-colors hover:bg-[#D9A441]/10"
                                >
                                    <LogOut className="h-4 w-4" />
                                    Lock
                                </button>
                            </div>
                        </div>

                        <div className="mt-8 overflow-hidden rounded-2xl border border-[#D9A441]/25 bg-[#0B0E18]/80 backdrop-blur-xl">
                            <table data-testid="admin-table" className="w-full text-left text-sm">
                                <thead>
                                    <tr className="border-b border-[#D9A441]/20 text-[11px] uppercase tracking-[0.18em] text-[#D9A441]">
                                        <th className="px-6 py-4 font-semibold">Email</th>
                                        <th className="px-6 py-4 font-semibold">Joined</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {subscribers.map((sub) => (
                                        <tr key={sub.id || sub.email} className="border-b border-white/5 text-[#F8F5EE]/75 transition-colors hover:bg-white/[0.03]">
                                            <td className="px-6 py-3.5">{sub.email}</td>
                                            <td className="px-6 py-3.5 text-[#F8F5EE]/50">
                                                {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleString() : "—"}
                                            </td>
                                        </tr>
                                    ))}
                                    {subscribers.length === 0 && (
                                        <tr>
                                            <td colSpan={2} className="px-6 py-10 text-center text-[#F8F5EE]/40">
                                                No subscribers yet.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
};

export default AdminPage;
